/**
 * Page PCED (Procédure de Contrôle des Energies Dangereuses) : liste
 * triable/filtrable/recherchable/exportable.
 */
import { useEffect, useState } from "react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import useQuickFilter from "@/hooks/useQuickFilter";
import DataTable from "@/components/tables/DataTable";
import SummaryChip from "@/components/tables/SummaryChip";
import SiteProgressPanel from "@/components/pced/SiteProgressPanel";
import IdLinkCell from "@/components/tables/IdLinkCell";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PAGES, QUICK_FILTER_KPI } from "@/constants/testIds";

const COLUMNS = [
  { key: "id", label: "Numéro" },
  { key: "site", label: "Site" },
  { key: "responsible_name", label: "Responsable" },
  { key: "date", label: "Date" },
  { key: "task_description", label: "Description de la tâche", width: 280, wrap: true },
  { key: "activities", label: "Activités", width: 220, wrap: true },
  { key: "status", label: "Statut" },
];

const QUICK_FILTERS = {
  validated: (r) => r.status === "Validé",
  pending: (r) => r.status !== "Validé",
};

export default function PCEDPage() {
  const { filters, refreshKey } = useFilters();
  const [data, setData] = useState(null);
  const { active: quickFilter, toggle: toggleQuickFilter, clear: clearQuickFilter, apply: applyQuickFilter } = useQuickFilter(QUICK_FILTERS);

  useEffect(() => {
    dashboardApi.getPced(filters).then(({ data }) => setData(data));
  }, [filters, refreshKey]);

  const renderCell = (col, row) => {
    if (col.key === "id") {
      return <IdLinkCell id={row.id} href={row.link} testId={`pced-link-${row.id}`} />;
    }
    if (col.key === "status") {
      return (
        <Badge variant="outline" className={row.status === "Validé" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>
          {row.status}
        </Badge>
      );
    }
    return undefined;
  };

  if (!data) return <Skeleton className="h-96 rounded-lg" />;

  return (
    <div data-testid={PAGES.pced} className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">PCED — Procédure de Contrôle des Energies Dangereuses</h1>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryChip testId={QUICK_FILTER_KPI.pcedTotal} label="Total" value={data.summary.total} onClick={clearQuickFilter} active={!quickFilter} />
        <SummaryChip testId={QUICK_FILTER_KPI.pcedValidated} label="Validées" value={data.summary.validated} tone="green" onClick={() => toggleQuickFilter("validated")} active={quickFilter === "validated"} />
        <SummaryChip testId={QUICK_FILTER_KPI.pcedPending} label="En attente" value={data.summary.pending} tone="orange" onClick={() => toggleQuickFilter("pending")} active={quickFilter === "pending"} />
        <SummaryChip label="Objectif" value={data.summary.objective} tone="blue" />
      </div>
      <SiteProgressPanel />
      <DataTable testId="pced-table" title="PCED" columns={COLUMNS} data={applyQuickFilter(data.items)} renderCell={renderCell} />
    </div>
  );
}
